import { useState } from "react";
import React from "react";
import { getFirestore, setDoc, doc } from "firebase/firestore";
import app from "./firebase";
import emailjs from "emailjs-com";
import { Helmet } from "react-helmet";
import {
    Leaf, User, Mail, Lock, Phone, Calendar, Globe,
    Eye, EyeOff, CheckCircle2, AlertCircle, ArrowRight, ChevronRight
} from "lucide-react";

const db = getFirestore(app);

const Registration = () => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirm_password, setConfirmPassword] = useState("");
    const [phone, setPhone] = useState("");
    const [successPanel, setSuccessPanel] = useState(false);
    const [errorPanel, setErrorPanel] = useState(false);
    const [error_message, setErrorMessage] = useState("");
    const [passwordMatch, setPasswordMatch] = useState(null); // null | "match" | "no-match"
    const [dob, setDOB] = useState("01-01-1970");
    const [nationality, setNationality] = useState("");
    const [showPass, setShowPass] = useState(false);
    const [showConfirmPass, setShowConfirmPass] = useState(false);
    const [loading, setLoading] = useState(false);
    const [agreed, setAgreed] = useState(false);

    const updateConfirmPassword = (event) => {
        const val = event.target.value;
        setConfirmPassword(val);
        if (val.length > 0) setPasswordMatch(val === password ? "match" : "no-match");
        else setPasswordMatch(null);
    };

    const showError = (msg) => {
        setErrorMessage(msg);
        setErrorPanel(true);
        setSuccessPanel(false);
        setTimeout(() => setErrorPanel(false), 5000);
    };

    const register = async () => {
        if (!agreed) { showError("Please agree to the Terms and Privacy Policy to continue."); return; }
        if (name.length < 3) { showError("Please enter your full name (at least 3 characters)."); return; }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) { showError("Please enter a valid email address."); return; }
        if (password.length < 6) { showError("Password must be at least 6 characters."); return; }
        if (password !== confirm_password) { showError("Passwords do not match."); return; }
        if (phone.length < 5) { showError("Please enter a valid phone number."); return; }
        if (!nationality) { showError("Please select your country."); return; }
        setLoading(true);
        try {
            const userid = email.replace("@", "").replace(".", "").toLowerCase();
            const otp = Math.floor(100000 + Math.random() * 900000).toString();

            await setDoc(doc(db, "users", userid), {
                name, email, password, phone, DOB: dob,
                Nationality: nationality, wallet: 0, investment: 0,
                factor2: true, status: "active",
                emailVerified: false,
                emailOTP: otp,
                registrationDate: new Date().toISOString()
            });
            await setDoc(doc(db, "notification", userid), { price_change: false, signin: false, transaction: false });

            // Send OTP via EmailJS
            const templateParams = {
                header: "AGRIVESTIA VERIFICATION",
                subject: "WELCOME TO AGRIVESTIA - EMAIL VERIFICATION",
                message: `Hi ${name}, welcome to Agrivestia! Your email verification code is: ${otp}`,
                email: email
            };
            emailjs.send("service_pehhhv8", "template_rql77hn", templateParams, "9o1vflADP-wePeEFd")
                .catch(err => console.error("Email error:", err));

            setSuccessPanel(true);
            setTimeout(() => { window.location.href = `/verify_email?email=${encodeURIComponent(email)}`; }, 1500);
        } catch (error) {
            showError("Registration failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const countries = [
        "Afghanistan", "Albania", "Algeria", "Angola", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
        "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia",
        "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde",
        "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros",
        "Congo (Congo-Brazzaville)", "Congo (Congo-Kinshasa)", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
        "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea",
        "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany",
        "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hungary",
        "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan",
        "Kazakhstan", "Kenya", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya",
        "Lithuania", "Luxembourg", "Madagascar", "Malaysia", "Maldives", "Mali", "Malta", "Mexico", "Mongolia", "Morocco",
        "Mozambique", "Myanmar", "Namibia", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria",
        "Norway", "Pakistan", "Palestine", "Panama", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar",
        "Romania", "Russia", "Rwanda", "South Africa", "United Kingdom", "United States"
    ];

    return (
        <div style={{ minHeight: "100vh", display: "flex", fontFamily: "var(--font-sans)" }}>
            <Helmet>
                <title>Create Account — Agrivestia | Invest in Agriculture</title>
                <meta name="description" content="Join Agrivestia and start investing in Africa's agricultural marketplace today." />
            </Helmet>

            {/* ── Left brand panel ── */}
            <div className="hide-mobile" style={{
                width: "42%", background: "var(--gradient-hero)",
                display: "flex", flexDirection: "column", justifyContent: "center",
                padding: "60px 50px", position: "relative", overflow: "hidden",
            }}>
                <div style={{ position: "absolute", inset: 0, opacity: 0.05, backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
                <div style={{ position: "absolute", top: "-60px", left: "-60px", width: "400px", height: "400px", background: "radial-gradient(circle, rgba(34,197,94,0.15) 0%, transparent 70%)" }} />
                <div style={{ position: "relative" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "48px" }}>
                        <div style={{ width: "40px", height: "40px", borderRadius: "12px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Leaf size={20} color="white" />
                        </div>
                        <span style={{ fontWeight: 800, fontSize: "22px", color: "white", letterSpacing: "-0.02em" }}>AGRIVESTIA</span>
                    </div>
                    <h1 style={{ fontSize: "clamp(26px,3vw,40px)", fontWeight: 900, color: "white", lineHeight: 1.2, marginBottom: "16px", letterSpacing: "-0.02em" }}>
                        Join Africa's{" "}
                        <span style={{ background: "linear-gradient(135deg, #22c55e, #86efac)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                            #1 AgriFinTech
                        </span>
                        {" "}Platform
                    </h1>
                    <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.7)", lineHeight: 1.7, marginBottom: "36px", maxWidth: "320px" }}>
                        Create your free account today and start investing in crops, trading commodities, and growing wealth from anywhere.
                    </p>

                    {[
                        "Zero setup fees — free to join",
                        "Live dashboard with real-time data",
                        "Trade premium African crops",
                        "Secure wallet, instant withdrawals",
                    ].map((text, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                            <CheckCircle2 size={16} color="#22c55e" />
                            <span style={{ fontSize: "14px", color: "rgba(255,255,255,0.8)", fontWeight: 500 }}>{text}</span>
                        </div>
                    ))}

                    <div style={{ marginTop: "36px", padding: "18px", background: "rgba(255,255,255,0.06)", borderRadius: "var(--radius-lg)", border: "1px solid rgba(255,255,255,0.1)" }}>
                        <p style={{ fontSize: "14px", color: "rgba(255,255,255,0.85)", lineHeight: 1.6, margin: 0, fontStyle: "italic" }}>
                            "Agrivestia transformed how I invest. I doubled my portfolio in 8 months from home."
                        </p>
                        <div style={{ marginTop: "12px", fontSize: "12px", color: "rgba(255,255,255,0.5)", fontWeight: 600 }}>— Adesola Okafor, Lagos</div>
                    </div>

                    <div style={{ marginTop: "28px", display: "flex", gap: "24px", alignItems: "center" }}>
                        <div>
                            <span style={{ fontSize: "14px", color: "rgba(255,255,255,0.6)" }}>Already have an account? </span>
                            <span onClick={() => window.location.href = "/login"} style={{ fontSize: "14px", color: "#22c55e", fontWeight: 600, cursor: "pointer" }}>Sign In →</span>
                        </div>
                        <div onClick={() => window.location.href = "/blog"} style={{ fontSize: "14px", color: "rgba(255,255,255,0.8)", fontWeight: 600, cursor: "pointer", borderLeft: "1px solid rgba(255,255,255,0.2)", paddingLeft: "24px" }}>
                            Read Blog →
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Right form panel ── */}
            <div style={{
                flex: 1, background: "var(--color-gray-50)",
                display: "flex", flexDirection: "column",
                justifyContent: "center", alignItems: "center",
                padding: "40px clamp(20px,4vw,60px)", overflowY: "auto",
            }}>
                {/* Mobile logo */}
                <div className="show-mobile" style={{ display: "none", alignItems: "center", gap: "8px", marginBottom: "28px" }}>
                    <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Leaf size={16} color="white" />
                    </div>
                    <span style={{ fontWeight: 800, fontSize: "18px", color: "var(--color-primary-dark)" }}>AGRIVESTIA</span>
                </div>

                <div style={{ width: "100%", maxWidth: "460px" }}>
                    <div style={{ marginBottom: "28px" }}>
                        <h2 style={{ fontSize: "26px", fontWeight: 800, color: "var(--color-gray-900)", marginBottom: "6px", letterSpacing: "-0.02em" }}>Create Your Account</h2>
                        <p style={{ fontSize: "14px", color: "var(--color-gray-500)" }}>Fill in your details below to get started</p>
                    </div>

                    {/* Error */}
                    {errorPanel && (
                        <div style={{ display: "flex", gap: "10px", padding: "12px 14px", background: "var(--color-danger-light)", borderRadius: "var(--radius-md)", border: "1px solid #fca5a5", marginBottom: "20px", animation: "fadeInUp 0.3s ease" }}>
                            <AlertCircle size={16} color="#dc2626" style={{ flexShrink: 0, marginTop: "1px" }} />
                            <p style={{ fontSize: "13px", color: "#991b1b", margin: 0 }}>{error_message}</p>
                        </div>
                    )}

                    {/* Success */}
                    {successPanel && (
                        <div style={{ display: "flex", gap: "10px", padding: "12px 14px", background: "var(--color-success-light)", borderRadius: "var(--radius-md)", border: "1px solid #86efac", marginBottom: "20px", animation: "fadeInUp 0.3s ease" }}>
                            <CheckCircle2 size={16} color="#16a34a" />
                            <p style={{ fontSize: "13px", color: "#15803d", margin: 0, fontWeight: 500 }}>Account created! Redirecting to login…</p>
                        </div>
                    )}

                    {/* ─ Personal Info ─ */}
                    <div style={{ marginBottom: "8px", paddingBottom: "4px", borderBottom: "1px solid var(--color-gray-200)", marginTop: "4px" }}>
                        <span style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-primary)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Personal Information</span>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "0" }}>
                        <div className="input-group" style={{ marginTop: "16px" }}>
                            <label className="input-label">Full Name</label>
                            <div style={{ position: "relative" }}>
                                <User size={15} color="var(--color-gray-400)" style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)" }} />
                                <input type="text" className="input-field" onChange={e => setName(e.target.value)} placeholder="John Doe" style={{ paddingLeft: "40px" }} />
                            </div>
                        </div>

                        <div className="input-group">
                            <label className="input-label">Email Address</label>
                            <div style={{ position: "relative" }}>
                                <Mail size={15} color="var(--color-gray-400)" style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)" }} />
                                <input type="email" className="input-field" onChange={e => setEmail(e.target.value)} placeholder="you@example.com" style={{ paddingLeft: "40px" }} />
                            </div>
                        </div>

                        <div className="input-group">
                            <label className="input-label">Phone Number</label>
                            <div style={{ position: "relative" }}>
                                <Phone size={15} color="var(--color-gray-400)" style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)" }} />
                                <input type="text" className="input-field" onChange={e => setPhone(e.target.value)} placeholder="+234 800 000 0000" style={{ paddingLeft: "40px" }} />
                            </div>
                        </div>
                    </div>

                    {/* ─ Security ─ */}
                    <div style={{ marginBottom: "8px", paddingBottom: "4px", borderBottom: "1px solid var(--color-gray-200)", marginTop: "8px" }}>
                        <span style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-primary)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Security</span>
                    </div>

                    <div style={{ marginTop: "16px" }}>
                        <div className="input-group">
                            <label className="input-label">Password</label>
                            <div style={{ position: "relative" }}>
                                <Lock size={15} color="var(--color-gray-400)" style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)" }} />
                                <input type={showPass ? "text" : "password"} className="input-field" onChange={e => setPassword(e.target.value)} placeholder="Min. 6 characters" style={{ paddingLeft: "40px", paddingRight: "42px" }} />
                                <button onClick={() => setShowPass(!showPass)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-400)" }}>
                                    {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                                </button>
                            </div>
                        </div>

                        <div className="input-group">
                            <label className="input-label">Confirm Password</label>
                            <div style={{ position: "relative" }}>
                                <Lock size={15} color="var(--color-gray-400)" style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)" }} />
                                <input type={showConfirmPass ? "text" : "password"} className="input-field" onChange={updateConfirmPassword} placeholder="Repeat your password"
                                    style={{ paddingLeft: "40px", paddingRight: "42px", borderColor: passwordMatch === "match" ? "#22c55e" : passwordMatch === "no-match" ? "#ef4444" : undefined }} />
                                <button onClick={() => setShowConfirmPass(!showConfirmPass)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-400)" }}>
                                    {showConfirmPass ? <EyeOff size={15} /> : <Eye size={15} />}
                                </button>
                            </div>
                            {passwordMatch && (
                                <div style={{ display: "flex", alignItems: "center", gap: "5px", marginTop: "4px" }}>
                                    {passwordMatch === "match"
                                        ? <><CheckCircle2 size={13} color="#16a34a" /><span style={{ fontSize: "12px", color: "#16a34a", fontWeight: 500 }}>Passwords match</span></>
                                        : <><AlertCircle size={13} color="#dc2626" /><span style={{ fontSize: "12px", color: "#dc2626", fontWeight: 500 }}>Passwords don't match</span></>}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ─ Location ─ */}
                    <div style={{ marginBottom: "8px", paddingBottom: "4px", borderBottom: "1px solid var(--color-gray-200)", marginTop: "8px" }}>
                        <span style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-primary)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Location</span>
                    </div>

                    <div style={{ marginTop: "16px" }}>
                        <div className="input-group">
                            <label className="input-label">Date of Birth</label>
                            <div style={{ position: "relative" }}>
                                <Calendar size={15} color="var(--color-gray-400)" style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)" }} />
                                <input type="date" className="input-field" onChange={e => setDOB(e.target.value)} defaultValue="1990-01-01" style={{ paddingLeft: "40px" }} />
                            </div>
                        </div>

                        <div className="input-group">
                            <label className="input-label">Country / Nationality</label>
                            <div style={{ position: "relative" }}>
                                <Globe size={15} color="var(--color-gray-400)" style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)" }} />
                                <select className="input-select" onChange={e => setNationality(e.target.value)} style={{ paddingLeft: "40px" }}>
                                    <option value="">Select a country</option>
                                    {countries.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Terms */}
                    <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer", marginBottom: "24px" }}>
                        <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                            style={{ width: "16px", height: "16px", marginTop: "2px", accentColor: "var(--color-primary)", cursor: "pointer", flexShrink: 0 }} />
                        <span style={{ fontSize: "13px", color: "var(--color-gray-600)", lineHeight: 1.5 }}>
                            I have read and agree to Agrivestia's{" "}
                            <span style={{ color: "var(--color-primary)", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }} onClick={() => window.open('/terms-of-service', '_blank')}>Terms of Service</span> and{" "}
                            <span style={{ color: "var(--color-primary)", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }} onClick={() => window.open('/privacy-policy', '_blank')}>Privacy Policy</span>.
                        </span>
                    </label>

                    {/* Submit */}
                    <button className="btn-primary" onClick={register} disabled={loading}
                        style={{ width: "100%", justifyContent: "center", padding: "14px", fontSize: "16px", opacity: loading ? 0.7 : 1 }}>
                        {loading
                            ? <><span className="spinner" style={{ width: "18px", height: "18px", borderWidth: "2px" }} /> Creating Account…</>
                            : <>Create Account <ArrowRight size={18} /></>}
                    </button>

                    <p style={{ textAlign: "center", marginTop: "20px", fontSize: "13px", color: "var(--color-gray-500)" }}>
                        Already have an account?{" "}
                        <span onClick={() => window.location.href = "/login"} style={{ color: "var(--color-primary)", fontWeight: 600, cursor: "pointer" }}>Sign In →</span>
                    </p>
                </div>

                <style>{`
                `}</style>
            </div>
        </div>
    );
};

export default Registration;